import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Check, X, Flame, Building2, MapPin, Sparkles, Undo2 } from "lucide-react";
import { investorQuickAction, investorUndoAction } from "../../lib/api";
import { TierBadge, PhaseBadge, EinstufungBadge } from "./Badges";

/**
 * Welle 5 — InvestorSpeedCard
 * Kompakte Investor-Karte mit 4 Quick-Action-Buttons (rechts, Hover-sichtbar).
 * Actions:
 *  - ↑ Pin Up
 *  - ↓ Pin Down
 *  - ✓ Advance (nächste Phase)
 *  - ✗ Disqualify
 * Bei jeder Aktion: Optimistic UI, Undo-Toast (5 s).
 */

// Icon + Label je nach Wärmegrad
function EinstufungIcon({ e }) {
  if (e === "Heiss" || e === "Heiß") return <Flame size={12} className="text-tot" />;
  if (e === "Reaktivierbar") return <Sparkles size={12} className="text-gold" />;
  return null;
}

// Highlight-Fragment: Findet ein Vorkommen von `q` in `text` und rendert es mit
// gelbem Hintergrund. Case-insensitive.
function Highlighted({ text, q }) {
  if (!q || !text) return <>{text}</>;
  const idx = String(text).toLowerCase().indexOf(String(q).toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-gold/40 text-navy px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function EngagementBar({ score }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const color = s >= 60 ? "bg-tot" : s >= 30 ? "bg-gold" : "bg-navy/25";
  return (
    <div className="flex items-center gap-1 min-w-[62px]" title={`Engagement-Score ${s}/100`}>
      <div className="h-1 flex-1 bg-navy/10 relative overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${color}`} style={{ width: `${s}%` }} />
      </div>
      <span className="text-[10px] text-navy/55 tabular-nums font-sans">{s}</span>
    </div>
  );
}

export default function InvestorSpeedCard({
  investor,
  searchQuery = "",
  speedMode = false,
  focused = false,
  onOpenDrawer,
  onLocalUpdate,
  onSelect,
  selected = false,
}) {
  const [busy, setBusy] = useState(null); // aktuell laufende Action

  const doAction = async (action) => {
    if (busy) return;
    // Optimistic hint für einige Actions
    const prev = { ...investor };
    setBusy(action);
    try {
      const updated = await investorQuickAction(investor.id, action);
      onLocalUpdate?.(updated);
      const label = {
        advance: `→ ${updated.phase}`,
        regress: `← ${updated.phase}`,
        disqualify: "→ Abgesagt",
        pin_up: "gepinnt (oben)",
        pin_down: "gepinnt (unten)",
        unpin: "Pin entfernt",
      }[action] || "Aktion ausgeführt";
      toast.success(
        <div className="flex items-center gap-3">
          <span>{updated.firma}: {label}</span>
          <button
            data-testid="undo-quick-action-btn"
            className="text-xs uppercase tracking-widish text-gold hover:text-white hover:bg-gold px-2 py-1 border border-gold/40"
            onClick={async (ev) => {
              ev.preventDefault(); ev.stopPropagation();
              try {
                const restored = await investorUndoAction(investor.id);
                onLocalUpdate?.(restored);
                toast.dismiss();
                toast.success("Aktion rückgängig gemacht");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Undo fehlgeschlagen");
                onLocalUpdate?.(prev);
              }
            }}
          >
            <Undo2 size={10} className="inline mr-1" /> Rückgängig
          </button>
        </div>,
        { duration: 5000 },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  const ap = (investor.ansprechpartner || [])[0];
  const cardBg =
    focused
      ? "bg-gold/[0.06] ring-1 ring-gold/40"
      : selected
        ? "bg-navy/[0.03] ring-1 ring-navy/20"
        : "bg-white";

  return (
    <div
      data-testid={`speed-card-${investor.id}`}
      data-focused={focused ? "true" : "false"}
      className={`relative group border border-navy/12 ${cardBg} transition-shadow duration-150 hover:shadow-md ${
        speedMode ? "p-2.5" : "p-3.5"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        {onSelect && (
          <input
            type="checkbox"
            data-testid={`speed-card-select-${investor.id}`}
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(investor.id);
            }}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 accent-[#022750]"
          />
        )}
        {/* Body */}
        <button
          type="button"
          data-testid={`speed-card-open-${investor.id}`}
          onClick={() => onOpenDrawer?.(investor.id)}
          className="flex-1 min-w-0 text-left"
        >
          {/* Titel */}
          <div className="flex items-center gap-2 flex-wrap">
            <TierBadge tier={investor.tier} size="sm" />
            <span className={`font-display font-semibold text-navy leading-tight ${
              speedMode ? "text-[13px]" : "text-sm"
            }`}>
              <Highlighted text={investor.firma} q={searchQuery} />
            </span>
            {(investor.custom_priority || 0) < 0 && (
              <span title="Nach oben gepinnt" className="text-gold" data-testid="speed-card-pinned-up">
                <ChevronUp size={12} />
              </span>
            )}
            {(investor.custom_priority || 0) > 0 && (
              <span title="Nach unten gepinnt" className="text-navy/40" data-testid="speed-card-pinned-down">
                <ChevronDown size={12} />
              </span>
            )}
          </div>

          {/* Meta */}
          {!speedMode && (
            <div className="text-[11px] text-navy/60 font-sans mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
              {investor.investorentyp_kategorie && (
                <span className="inline-flex items-center gap-1"><Building2 size={10} />{investor.investorentyp_kategorie}</span>
              )}
              {investor.investorentyp_subtyp && <span>· {investor.investorentyp_subtyp}</span>}
              {investor.stadt && (
                <span className="inline-flex items-center gap-1"><MapPin size={10} />{investor.stadt}</span>
              )}
            </div>
          )}

          {/* Ansprechpartner (nur nicht-speed) */}
          {!speedMode && ap && (ap.vorname || ap.nachname) && (
            <div
              data-testid={`speed-card-ap-${investor.id}`}
              className="text-[11px] text-navy/70 font-sans mt-1"
            >
              <span className="text-navy/40 mr-1">👤</span>
              <Highlighted text={`${ap.vorname || ""} ${ap.nachname || ""}`.trim()} q={searchQuery} />
              {ap.funktion && (
                <>
                  <span className="text-navy/40 mx-1">·</span>
                  <Highlighted text={ap.funktion} q={searchQuery} />
                </>
              )}
            </div>
          )}

          {/* Row: einstufung + phase + engagement */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <EinstufungIcon e={investor.einstufung} />
            <EinstufungBadge value={investor.einstufung} />
            <PhaseBadge phase={investor.phase} />
            <EngagementBar score={investor.engagement_score} />
          </div>
        </button>

        {/* Quick-Action-Buttons — immer sichtbar auf touch, opacity-fade auf Desktop */}
        <div
          data-testid="speed-card-actions"
          className="flex flex-col gap-1 shrink-0 opacity-100 sm:opacity-30 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150"
        >
          <button
            data-testid={`quick-action-pin-up-${investor.id}`}
            disabled={busy !== null}
            onClick={(e) => { e.stopPropagation(); doAction("pin_up"); }}
            title="Nach oben priorisieren"
            className="p-1.5 border border-navy/15 bg-white text-navy/70 hover:bg-gold hover:text-white hover:border-gold min-w-[32px] min-h-[32px] inline-flex items-center justify-center"
          >
            <ChevronUp size={14} />
          </button>
          <button
            data-testid={`quick-action-pin-down-${investor.id}`}
            disabled={busy !== null}
            onClick={(e) => { e.stopPropagation(); doAction("pin_down"); }}
            title="Nach unten deprio"
            className="p-1.5 border border-navy/15 bg-white text-navy/70 hover:bg-navy hover:text-white hover:border-navy min-w-[32px] min-h-[32px] inline-flex items-center justify-center"
          >
            <ChevronDown size={14} />
          </button>
          <button
            data-testid={`quick-action-advance-${investor.id}`}
            disabled={busy !== null}
            onClick={(e) => { e.stopPropagation(); doAction("advance"); }}
            title="Qualifiziert · Phase weiter"
            className="p-1.5 border border-emerald-500/50 bg-emerald-50 text-emerald-800 hover:bg-emerald-600 hover:text-white min-w-[32px] min-h-[32px] inline-flex items-center justify-center"
          >
            <Check size={14} />
          </button>
          <button
            data-testid={`quick-action-disqualify-${investor.id}`}
            disabled={busy !== null}
            onClick={(e) => { e.stopPropagation(); doAction("disqualify"); }}
            title="Disqualifizieren · Phase Abgesagt"
            className="p-1.5 border border-tot/50 bg-tot/5 text-tot hover:bg-tot hover:text-white min-w-[32px] min-h-[32px] inline-flex items-center justify-center"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
